/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Component, Input, OnInit } from '@angular/core';
import { ChartsHelper } from '@app/app/shared/helpers/charts-helper';
import { ChartDataset } from 'chart.js';
import { cloneDeep } from 'lodash-es';
import { LocalTimeHelper } from '../../../../helpers/local-time-helper';
import { ChartContent, ChartItem, ChartVersionScore } from '../../../../models/editor/chart-content.model';

@Component({
  selector: 'ptl-bar-chart-preview',
  templateUrl: './bar-chart-preview.component.html',
  styleUrls: ['./bar-chart-preview.component.scss'],
})
export class BarChartPreviewComponent implements OnInit {
  @Input() chartContent: ChartContent;

  @Input() chartVersionStart: number;
  @Input() chartVersionEnd: number;

  chartData: ChartDataset[];
  chartDataLabels: string[];

  chartSectionItems: { title: string; sectionUid: string }[];

  chartId = new Date().getTime();

  iconExists: boolean;
  completedEndText: string;
  completedStartText: string;

  ngOnInit(): void {
    if (this.chartContent) {
      this.showBarChart();
      this.checkIfIconsExists();
      this.updateCompletionText();
    }
  }

  scrollTo(id: string) {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  updateChartDataByDates(): void {
    const latestScores = [];
    latestScores.push(this.chartContent.versionedScores.find((score) => this.chartVersionEnd === score.version));
    latestScores.push(this.chartContent.versionedScores.find((score) => this.chartVersionStart === score.version));
    const contentItems = this.chartContent.items.filter((item) => item.scored !== null);
    this.chartData = this.calculateScoresAndGetChartData(latestScores, contentItems);
    this.updateCompletionText();
  }

  private showBarChart(): void {
    const labels: string[] = [];
    const sectionItems: { title: string; sectionUid: string }[] = [];
    const contentItems = this.chartContent.items.filter((item) => item.scored !== null);
    for (const item of contentItems) {
      labels.push(item.title);
      sectionItems.push({ title: item.title, sectionUid: item.sectionUid });
    }
    const latestScores = cloneDeep(this.chartContent.versionedScores);
    if (latestScores.length > 2) {
      latestScores.length = 2;
    }

    this.chartData = this.calculateScoresAndGetChartData(latestScores, contentItems);
    this.chartDataLabels = labels;
    this.chartSectionItems = sectionItems;
  }

  private calculateScoresAndGetChartData(latestScores: ChartVersionScore[], contentItems: ChartItem[]): ChartDataset[] {
    const scoredData: ChartDataset[] = [];

    for (let i = 0; i < latestScores.length; i++) {
      const alphaColor = i > 0 ? 0.8 : 0.5;
      const scoredItem = latestScores[i];

      const items = scoredItem.scores.filter((item) => item.userScore !== null);
      const itemMap = new Map(items.map((item) => [item.tagId, item]));

      const scores: number[] = [];
      const bgColors: string[] = [];
      const borderColor: string[] = [];
      const hoverBackgroundColor: string[] = [];
      const hoverBorderColor: string[] = [];
      const hoverBorderWidth: number[] = [];

      for (let k = 0; k < contentItems.length; k++) {
        const content = contentItems[k];
        const item = itemMap.get(content.itemUid);
        if (item) {
          const score = Math.floor((item.userScore * 100) / item.maxScore);
          const cappedScore = score > 100 ? 100 : score;
          scores.push(cappedScore);
          const tpColor = ChartsHelper.getTransparentColor(content.color, alphaColor);
          const gradient = `repeating-linear-gradient(45deg, ${tpColor}, ${tpColor} 0.313rem, ${content.color} 0.125rem, ${content.color} 0.438rem)`;
          const background = i === 0 ? tpColor : gradient;
          bgColors.push(background);
          hoverBackgroundColor.push(background);
          borderColor.push(content.color);
          hoverBorderColor.push(content.color);
          hoverBorderWidth.push(1);
        }
      }

      if (scores.length) {
        scoredData.push({
          data: scores,
          backgroundColor: bgColors,
          hoverBorderColor: hoverBorderColor,
          hoverBorderWidth: hoverBorderWidth,
          hoverBackgroundColor: hoverBackgroundColor,
          borderColor: borderColor,
          borderWidth: 0, // Or any value you need
        });
      }
    }

    return scoredData;
  }

  private getLocalDateTimeFormat(date: Date): string {
    return LocalTimeHelper.getLocalDateTime(date).format('DD MMMM YYYY, HH:mm');
  }

  private updateCompletionText(): void {
    if ((this.chartContent && this.chartVersionEnd) || this.chartVersionStart) {
      if (this.chartVersionEnd) {
        this.completedEndText = this.getLocalDateTimeFormat(
          this.chartContent?.versionedScores.find((version) => version.version === this.chartVersionEnd).completedOn,
        );
      }
      if (this.chartVersionStart) {
        this.completedStartText = this.getLocalDateTimeFormat(
          this.chartContent?.versionedScores.find((version) => version.version === this.chartVersionStart).completedOn,
        );
      }
    }
  }

  private checkIfIconsExists(): void {
    this.iconExists = false;
    for (const item of this.chartContent.items) {
      if (item.icon && (item.icon.iconImageUrl || item.icon.iconClass)) {
        this.iconExists = true;
        break;
      }
    }
  }
}
